import { CubeIcon, StopIcon } from "@heroicons/react/20/solid";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button, ButtonIntent, ButtonState } from "src/components/Button";
import { observer } from "mobx-react-lite";
import { EditorInspector } from "./EditorInspector";
import { EditorMap } from "./EditorMap";
import type { Editor } from "src/types/Editor";
import type { WorkspaceConfig } from "src/hooks/useWorkspaceQuery";
import { useLiveRef } from "src/hooks/useLiveRef";
import { ExplorePanel } from "./ExplorePanel/ExplorePanel";
import { Plan2D } from "./Plan2D";
import { DevCostFixtureExport } from "src/dev/DevCostFixtureExport";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "src/contexts/api.context";
import { useActiveParams } from "src/hooks/useActiveParams";
import { useActiveProjectQuery } from "src/hooks/useActiveProjectQuery";
import { generateAndDownloadSummaryPdf } from "src/utils/generateAndDownloadSummaryPdf";
import { ToolSelection } from "src/types/MapState";
import { useFeatureFlag } from "src/contexts/posthog.context";
import { FLAGS } from "src/posthog/flags";

const getScenarioQueryKey = (projectId: string) => [projectId, "scenario"];

function useScenarioQuery(editor: Editor) {
  const projectId = useActiveParams().projectId ?? "";
  const api = useApi();
  return useQuery({
    queryKey: getScenarioQueryKey(projectId),
    queryFn: async () => {
      const scenario = await api.GetScenarioWithWorldFile(projectId);
      if (!scenario) return null;
      return await editor.loader.loadFromScenarioWithDesign(scenario);
    },
    enabled: !!projectId,
    staleTime: Infinity,
  });
}

export const DesignEditor = observer(function Editor(props: {
  editor: Editor;
  workspace: WorkspaceConfig;
}) {
  const { projectId } = useActiveParams();
  const [section, setSection] = useState<"3D" | "2D">("3D");
  // briefRedesign hides the Inspector — its zone controls + metrics drawer now
  // live in the Brief panel.
  const briefRedesign = useFeatureFlag(FLAGS.briefRedesign);
  const workspaceRef = useLiveRef(props.workspace);
  const api = useApi();
  const scnearioQuery = useScenarioQuery(props.editor);
  const queryClient = useQueryClient();
  const mapState = props.editor.mapState;

  // A paid Summary-PDF payment link redirects back here (via /checkout/return)
  // with ?paidReport=1. Once the world has loaded, regenerate + download the
  // report with defaults (project name, all sections), then clear the flag so a
  // refresh doesn't re-download. The wizard's name/section choices don't survive
  // the redirect, so this uses defaults by design.
  const project = useActiveProjectQuery();
  const [searchParams, setSearchParams] = useSearchParams();
  const paidReport = searchParams.get("paidReport") === "1";
  const paidDeliveredRef = useRef(false);
  useEffect(() => {
    if (paidDeliveredRef.current || !paidReport) return;
    const world = props.editor.world;
    if (!world || !projectId) return; // wait for the world to load
    paidDeliveredRef.current = true;
    void generateAndDownloadSummaryPdf({
      world,
      projectId,
      projectName: project.data?.name ?? world.label?.trim() ?? "Tessa project",
      api,
      mapState,
    }).catch((err) => console.error("paid report generation failed:", err));
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("paidReport");
        return next;
      },
      { replace: true }
    );
  }, [paidReport, props.editor, projectId, project.data?.name, api, mapState, setSearchParams]);
  // optimistic update on save. ScenarioQuery must contain the latest world from editor.
  // Return the unsubscribe: onAutoSaveFinished registers on the SHARED editor.autoSave,
  // so without cleanup the listener leaks when DesignEditor unmounts. It then keeps
  // firing setQueryData on every autosave after you leave the canvas — and in Studio
  // that remounts the brief map (disposing its WebGL contexts) on every zone edit,
  // which exhausts the browser's context budget and crashes the map.
  useEffect(() => {
    const unsubscribe = props.editor.autoSave.onAutoSaveFinished(() => {
      if (!projectId) return;
      queryClient.setQueryData(getScenarioQueryKey(projectId), () => props.editor.world);
    });
    return () => {
      unsubscribe();
    };
  }, [projectId, props.editor, queryClient]);

  // load/reload world from scenario
  useEffect(() => {
    (async () => {
      if (!projectId) return;
      const scenario = scnearioQuery.data;
      if (!scenario) return;

      await props.editor.loadFromWorld(scenario);
      mapState.setWorld(props.editor.world);
      if (!!props.editor.world?.hasZone) props.editor.view.explorePanel.toggleVisible(true);
      if (
        !!props.editor.world?.hasZone &&
        mapState.selectedTool.value.id === ToolSelection.SEARCH_PLACE
      ) {
        mapState.selectedTool.update(ToolSelection.SELECT);
      }
    })();
  }, [
    api,
    mapState,
    mapState.selectedTool,
    projectId,
    props.editor,
    scnearioQuery.data,
    workspaceRef,
  ]);

  return (
    <>
      <div className="flex flex-1 bg-white flex-col relative overflow-hidden">
        <div className="flex w-full h-full relative">
          <div className="top-2 flex items-center flex-row overflow-hidden flex-shrink-0 rounded-t border-black/10 absolute z-10 right-1/2 translate-x-1/2">
            <Button.Group>
              <Button
                label="3D"
                Icon={CubeIcon}
                intent={ButtonIntent.SELECT}
                state={section === "3D" && ButtonState.ACTIVE}
                onClick={() => setSection("3D")}
              />
              <Button
                label="2D"
                Icon={StopIcon}
                intent={ButtonIntent.SELECT}
                state={section === "2D" && ButtonState.ACTIVE}
                onClick={() => setSection("2D")}
              />
            </Button.Group>
          </div>
          <DevCostFixtureExport editor={props.editor} />
          {section === "3D" && <EditorMap editor={props.editor} />}
          {section === "2D" && !!props.editor.world && <Plan2D world={props.editor.world} />}
        </div>
        <ExplorePanel editor={props.editor} workspaceConfig={props.workspace} />
      </div>
      {!briefRedesign && <EditorInspector editor={props.editor} />}
    </>
  );
});
